import { PROXY, YES_NO, feeInfo, representationSection } from "modules/ebiForms/common";
import { EbiCondition, IEbiField, IEbiFieldOption, IEbiFormAttachment, IEbiFormSection } from "modules/ebiForms/typings";

// Bloki wspólne pakietu „Pomoc społeczna i świadczenia” (C1–C9): kto składa wniosek w sprawach z ustawy o pomocy społecznej,
// pełnomocnik bez opłaty skarbowej, osoby w gospodarstwie domowym / rodzinie, obywatelstwo i stan cywilny, dochody rodziny
// (wzory MRPiPS SR-1/FA-1, pkt 4–5) i pobyt za granicą. Opłaty skarbowej nie pobiera się w żadnej z tych spraw —
// także od pełnomocnictwa (art. 2 ust. 1 pkt 1 lit. a i c ustawy o opłacie skarbowej).

export const PROXY_FEE_FREE =
  "Pełnomocnictwo w tej sprawie jest zwolnione z opłaty skarbowej (art. 2 ust. 1 pkt 1 ustawy o opłacie skarbowej).";

/** Pełnomocnik (art. 33 KPA) bez opłaty skarbowej: sekcja jak w innych wnioskach, bez pytania o zwolnienie rodzinne. */
export const socialRepresentationSection = (): IEbiFormSection => {
  const base = representationSection();

  return {
    ...base,
    fields: [
      ...base.fields.filter((field) => field.code !== "pelnomocnictwoZwolnione"),
      { ...feeInfo("infoPelnomocnictwo", PROXY_FEE_FREE), visibleWhen: PROXY },
    ],
  };
};

/** Pełnomocnictwo bez dowodu opłaty skarbowej (sprawy alimentacyjne, świadczeń socjalnych i pomocy społecznej). */
export const socialProxyAttachment = (): IEbiFormAttachment => ({
  code: "pelnomocnictwo",
  label: "Pełnomocnictwo",
  hint: "Skan podpisanego pełnomocnictwa (art. 33 KPA) — bez opłaty skarbowej.",
  visibleWhen: PROXY,
  required: true,
});

// ── kto składa wniosek o pomoc społeczną (art. 102 ustawy o pomocy społecznej) ──

export const ON_BEHALF: EbiCondition = { field: "wImieniu", in: ["przedstawiciel", "zaZgoda", "pelnomocnik"] };
export const WITH_CONSENT: EbiCondition = { field: "wImieniu", equals: "zaZgoda" };
export const IN_OWN_NAME: EbiCondition = { field: "wImieniu", equals: "wlasnym" };

/**
 * Wniosek osoby zainteresowanej, jej przedstawiciela ustawowego albo innej osoby za jej zgodą (art. 102 ust. 1 ustawy
 * o pomocy społecznej), a także pełnomocnika (art. 33 KPA). Kod pola jak w `representationSection()`, więc działają
 * warunki PROXY i pełnomocnictwo.
 */
export const socialApplicantRoleSection = (personTitle = "osoby zainteresowanej"): IEbiFormSection => ({
  code: "reprezentacja",
  title: "Kto składa wniosek",
  fields: [
    {
      code: "wImieniu",
      type: "radio",
      label: "Składam wniosek jako",
      required: true,
      defaultValue: "wlasnym",
      options: [
        { value: "wlasnym", label: "osoba zainteresowana — we własnej sprawie" },
        { value: "przedstawiciel", label: `przedstawiciel ustawowy ${personTitle} (np. rodzic, opiekun prawny)` },
        { value: "zaZgoda", label: `inna osoba — za zgodą ${personTitle} (art. 102 ust. 1 ustawy o pomocy społecznej)` },
        { value: "pelnomocnik", label: `pełnomocnik ${personTitle} (art. 33 KPA)` },
      ],
    },
    { code: "osobaImie", type: "text", label: "Imię osoby zainteresowanej", required: true, visibleWhen: ON_BEHALF, width: "half", maxLength: 100 },
    { code: "osobaNazwisko", type: "text", label: "Nazwisko osoby zainteresowanej", required: true, visibleWhen: ON_BEHALF, width: "half", maxLength: 100 },
    { code: "osobaPesel", type: "pesel", label: "PESEL osoby zainteresowanej", required: true, visibleWhen: ON_BEHALF, width: "half" },
    { code: "osobaTelefon", type: "phone", label: "Telefon osoby zainteresowanej", visibleWhen: ON_BEHALF, width: "half", hint: "Nieobowiązkowo." },
    { code: "osobaAdres", type: "text", label: "Adres zamieszkania osoby zainteresowanej — ulica, numer", required: true, visibleWhen: ON_BEHALF, maxLength: 200 },
    { code: "osobaKodPocztowy", type: "postalCode", label: "Kod pocztowy", required: true, visibleWhen: ON_BEHALF, width: "third", placeholder: "00-000" },
    { code: "osobaMiejscowosc", type: "text", label: "Miejscowość", required: true, visibleWhen: ON_BEHALF, width: "twoThirds", maxLength: 100 },
    { ...feeInfo("infoPelnomocnictwo", PROXY_FEE_FREE), visibleWhen: PROXY },
  ],
});

/** Zgoda osoby zainteresowanej (art. 102) i pełnomocnictwo. */
export const socialRoleAttachments = (): IEbiFormAttachment[] => [
  {
    code: "zgodaOsoby",
    label: "Zgoda osoby zainteresowanej na złożenie wniosku w jej imieniu",
    hint: "Skan podpisanej zgody (art. 102 ust. 1 ustawy o pomocy społecznej).",
    visibleWhen: WITH_CONSENT,
    required: true,
  },
  socialProxyAttachment(),
];

// ── osoby: gospodarstwo domowe, rodzina ──

/** Pola jednej osoby (pozycja listy): imię, nazwisko, PESEL albo dokument, data urodzenia, pokrewieństwo. */
export const personFields = (relationLabel = "Stopień pokrewieństwa (relacja do wnioskodawcy)"): IEbiField[] => [
  { code: "imie", type: "text", label: "Imię", required: true, width: "half", maxLength: 100 },
  { code: "nazwisko", type: "text", label: "Nazwisko", required: true, width: "half", maxLength: 100 },
  { code: "pesel", type: "pesel", label: "PESEL", width: "half", hint: "Jeśli nadano." },
  { code: "dokument", type: "text", label: "Seria i numer dokumentu tożsamości", width: "half", maxLength: 30, hint: "Tylko gdy nie nadano numeru PESEL." },
  { code: "dataUrodzenia", type: "date", label: "Data urodzenia", required: true, width: "half" },
  { code: "pokrewienstwo", type: "text", label: relationLabel, required: true, width: "half", maxLength: 100, placeholder: "np. żona, syn" },
];

/** Lista osób (rodzina, gospodarstwo domowe, dzieci). */
export const personsField = (options: {
  code: string;
  label: string;
  itemLabel: string;
  hint?: string;
  required?: boolean;
  visibleWhen?: EbiCondition;
  requiredWhen?: EbiCondition;
  fields?: IEbiField[];
  max?: number;
}): IEbiField => ({
  code: options.code,
  type: "repeat",
  label: options.label,
  itemLabel: options.itemLabel,
  hint: options.hint,
  required: options.required,
  requiredWhen: options.requiredWhen,
  visibleWhen: options.visibleWhen,
  min: options.required ? 1 : 0,
  max: options.max ?? 15,
  fields: options.fields ?? personFields(),
});

export const HOUSEHOLD_SHARED: EbiCondition = { field: "gospodarstwo", equals: "wspolne" };

/** Gospodarstwo domowe: samodzielne albo wspólne z innymi osobami (lista osób). */
export const householdFields = (): IEbiField[] => [
  {
    code: "gospodarstwo",
    type: "radio",
    label: "Gospodarstwo domowe osoby zainteresowanej",
    required: true,
    options: [
      { value: "samodzielne", label: "prowadzone samodzielnie (osoba samotnie gospodarująca)" },
      { value: "wspolne", label: "prowadzone wspólnie z innymi osobami" },
    ],
  },
  personsField({
    code: "osobyWGospodarstwie",
    label: "Osoby we wspólnym gospodarstwie domowym",
    itemLabel: "Osoba",
    hint: "Bez osoby zainteresowanej — jej dane są wyżej.",
    required: true,
    visibleWhen: HOUSEHOLD_SHARED,
    fields: personFields("Stopień pokrewieństwa (relacja do osoby zainteresowanej)"),
  }),
];

// ── obywatelstwo, stan cywilny ──

export const FOREIGNER: EbiCondition = { field: "obywatelstwo", equals: "inne" };

const CIVIL_STATUS: IEbiFieldOption[] = [
  { value: "panna", label: "panna" },
  { value: "kawaler", label: "kawaler" },
  { value: "zamezna", label: "zamężna" },
  { value: "zonaty", label: "żonaty" },
  { value: "rozwiedziona", label: "rozwiedziona" },
  { value: "rozwiedziony", label: "rozwiedziony" },
  { value: "separacja", label: "w separacji (orzeczonej prawomocnym wyrokiem sądu)" },
  { value: "wdowa", label: "wdowa" },
  { value: "wdowiec", label: "wdowiec" },
];

/** Obywatelstwo i (opcjonalnie) stan cywilny wnioskodawcy — dane wnioskodawcy ze wzorów MRPiPS. */
export const citizenshipFields = (civilStatusWhen?: EbiCondition | true): IEbiField[] => [
  {
    code: "obywatelstwo",
    type: "radio",
    label: "Obywatelstwo",
    required: true,
    defaultValue: "polskie",
    width: "half",
    options: [
      { value: "polskie", label: "polskie" },
      { value: "inne", label: "inne" },
    ],
  },
  { code: "obywatelstwoInne", type: "text", label: "Jakie obywatelstwo", required: true, visibleWhen: FOREIGNER, width: "half", maxLength: 100 },
  ...(civilStatusWhen
    ? [
        {
          code: "stanCywilny",
          type: "select",
          label: "Stan cywilny",
          required: true,
          width: "half",
          options: CIVIL_STATUS,
          visibleWhen: civilStatusWhen === true ? undefined : civilStatusWhen,
        } satisfies IEbiField,
      ]
    : []),
];

// ── dochody rodziny (SR-1, SR-2, SR-4, FA-1: pkt 4–5) ──

export const INCOME = {
  maintenancePaid: { field: "alimentyPlacone", equals: "tak" },
  nonTaxed: { field: "dochodyNieopodatkowane", equals: "tak" },
  farm: { field: "dochodyRolne", equals: "tak" },
  lost: { field: "utrataDochodu", equals: "tak" },
  gained: { field: "uzyskanieDochodu", equals: "tak" },
} satisfies Record<string, EbiCondition>;

const and = (a: EbiCondition | undefined, b: EbiCondition): EbiCondition => (a ? { all: [a, b] } : b);

/**
 * Składki zdrowotne, alimenty płacone na osoby spoza rodziny, dochody nieopodatkowane i z gospodarstwa rolnego, utrata
 * i uzyskanie dochodu — brzmienie pytań jak we wzorach MRPiPS. `period` — okres, którego dotyczy wniosek (np. „okres
 * zasiłkowy”), `when` — gdy sekcja dotyczy tylko części wariantów.
 */
export const familyIncomeFields = (options: { period: string; nonTaxedForm: string; farmForm: string; when?: EbiCondition }): IEbiField[] => [
  {
    code: "ubezpieczenieZdrowotne",
    type: "checkboxes",
    label: "Organ, do którego są opłacane składki na ubezpieczenie zdrowotne za Ciebie i członków Twojej rodziny",
    required: true,
    visibleWhen: options.when,
    options: [
      { value: "zus", label: "Zakład Ubezpieczeń Społecznych" },
      { value: "krus", label: "Kasa Rolniczego Ubezpieczenia Społecznego" },
      { value: "inny", label: "inny" },
      { value: "brak", label: "brak — za nikogo z rodziny nie są odprowadzane składki" },
    ],
  },
  {
    code: "ubezpieczenieInny",
    type: "text",
    label: "Nazwa i adres właściwej jednostki",
    required: true,
    visibleWhen: and(options.when, { field: "ubezpieczenieZdrowotne", includes: "inny" }),
    maxLength: 300,
  },
  {
    code: "alimentyPlacone",
    type: "radio",
    label: "Czy Ty lub członkowie Twojej rodziny płaciliście alimenty na rzecz osób spoza rodziny?",
    required: true,
    visibleWhen: options.when,
    options: YES_NO,
  },
  {
    code: "alimentyPlaconeRok",
    type: "number",
    label: "Rok kalendarzowy",
    required: true,
    visibleWhen: and(options.when, INCOME.maintenancePaid),
    width: "third",
    min: 2000,
    max: 2100,
    hint: "Rok, z którego dochód stanowi podstawę ustalenia dochodu rodziny.",
  },
  {
    code: "alimentyPlaconeKwota",
    type: "number",
    label: "Łączna kwota alimentów świadczonych na rzecz innych osób w tym roku [zł]",
    required: true,
    visibleWhen: and(options.when, INCOME.maintenancePaid),
    width: "twoThirds",
    decimals: 2,
    min: 0,
  },
  {
    code: "dochodyNieopodatkowane",
    type: "radio",
    label: `Czy w roku kalendarzowym poprzedzającym ${options.period} Ty lub członkowie Twojej rodziny osiągnęliście dochody niepodlegające opodatkowaniu podatkiem dochodowym od osób fizycznych na zasadach określonych w art. 27, art. 30b, art. 30c, art. 30e i art. 30f ustawy o podatku dochodowym od osób fizycznych?`,
    hint: `W przypadku odpowiedzi „Tak” dołącz oświadczenie o dochodzie (załącznik ${options.nonTaxedForm}).`,
    required: true,
    visibleWhen: options.when,
    options: YES_NO,
  },
  {
    code: "dochodyRolne",
    type: "radio",
    label: `Czy w roku kalendarzowym poprzedzającym ${options.period} Ty lub członkowie Twojej rodziny osiągnęliście dochody z gospodarstwa rolnego?`,
    hint: `W przypadku odpowiedzi „Tak” dołącz oświadczenie o wielkości gospodarstwa rolnego (załącznik ${options.farmForm}).`,
    required: true,
    visibleWhen: options.when,
    options: YES_NO,
  },
  {
    code: "utrataDochodu",
    type: "radio",
    label: `Czy w roku kalendarzowym poprzedzającym ${options.period} lub po tym roku nastąpiła utrata dochodu przez członka Twojej rodziny?`,
    hint: "Np. utrata zatrudnienia, zasiłku lub stypendium dla bezrobotnych, emerytury lub renty, wykreślenie działalności gospodarczej, uzyskanie prawa do urlopu wychowawczego.",
    required: true,
    visibleWhen: options.when,
    options: YES_NO,
  },
  {
    code: "uzyskanieDochodu",
    type: "radio",
    label: `Czy w roku kalendarzowym poprzedzającym ${options.period} lub po tym roku nastąpiło uzyskanie dochodu przez członka Twojej rodziny?`,
    hint: "Np. podjęcie zatrudnienia, uzyskanie zasiłku dla bezrobotnych, emerytury lub renty, rozpoczęcie działalności gospodarczej, zakończenie urlopu wychowawczego.",
    required: true,
    visibleWhen: options.when,
    options: YES_NO,
  },
];

/** Załączniki sterowane pytaniami o dochody rodziny. */
export const familyIncomeAttachments = (options: { nonTaxedForm: string; farmForm: string; when?: EbiCondition }): IEbiFormAttachment[] => [
  {
    code: "oswiadczenieDochodNieopodatkowany",
    label: `Oświadczenie o dochodzie niepodlegającym opodatkowaniu (${options.nonTaxedForm})`,
    hint: "Wypełniony i podpisany formularz — dla każdego członka rodziny, który osiągnął taki dochód.",
    visibleWhen: and(options.when, INCOME.nonTaxed),
    required: true,
    multiple: true,
  },
  {
    code: "oswiadczenieGospodarstwo",
    label: `Oświadczenie o wielkości gospodarstwa rolnego (${options.farmForm}) albo zaświadczenie gminy lub nakaz płatniczy`,
    hint: "Przy dzierżawie — także umowa dzierżawy (kopia).",
    visibleWhen: and(options.when, INCOME.farm),
    required: true,
    multiple: true,
  },
  {
    code: "alimentyPlaconeDowody",
    label: "Przekazy lub przelewy pieniężne dokumentujące alimenty płacone na rzecz osób spoza rodziny",
    visibleWhen: and(options.when, INCOME.maintenancePaid),
    required: true,
    multiple: true,
  },
  {
    code: "utrataDochoduDokument",
    label: "Dokument określający datę utraty dochodu i jego wysokość",
    hint: "Np. świadectwo pracy, decyzja o utracie zasiłku, PIT-11 (kopia).",
    visibleWhen: and(options.when, INCOME.lost),
    required: true,
    multiple: true,
  },
  {
    code: "uzyskanieDochoduDokument",
    label: "Dokument określający datę uzyskania dochodu i wysokość dochodu z miesiąca następującego po miesiącu jego uzyskania",
    hint: "Np. umowa, zaświadczenie pracodawcy o wynagrodzeniu (kopia).",
    visibleWhen: and(options.when, INCOME.gained),
    required: true,
    multiple: true,
  },
];

// ── pobyt za granicą (koordynacja systemów zabezpieczenia społecznego) ──

export const ABROAD: EbiCondition = { field: "pobytZaGranica", equals: "przebywa" };

export const abroadFields = (when?: EbiCondition): IEbiField[] => [
  {
    code: "pobytZaGranica",
    type: "radio",
    label: "Pobyt za granicą w państwie, w którym mają zastosowanie przepisy o koordynacji systemów zabezpieczenia społecznego",
    hint: "Nie dotyczy wyjazdu lub pobytu turystycznego, leczniczego ani związanego z kształceniem dziecka za granicą. Państwa koordynacji: UE, Norwegia, Islandia, Liechtenstein, Szwajcaria.",
    required: true,
    visibleWhen: when,
    options: [
      { value: "nie", label: "nie przebywam ani członek mojej rodziny (w tym współmałżonek lub drugi rodzic dziecka) nie przebywa za granicą" },
      { value: "przebywa", label: "przebywam lub członek mojej rodziny (w tym współmałżonek lub drugi rodzic dziecka) przebywa za granicą" },
    ],
  },
  {
    code: "pobytZaGranicaDane",
    type: "textarea",
    label: "Dane członków rodziny przebywających za granicą",
    hint: "Imię, nazwisko i (jeżeli są znane): PESEL, zagraniczny numer identyfikacyjny, państwo i okres pobytu, adres za granicą, nazwa i adres pracodawcy lub miejsce prowadzenia działalności.",
    required: true,
    visibleWhen: and(when, ABROAD),
    maxLength: 3000,
  },
];

/** Rachunek do wypłaty świadczenia (pole nieobowiązkowe, jak we wzorach MRPiPS). */
export const bankAccountField = (label = "Numer rachunku bankowego (lub w SKOK), na który ma być wypłacane świadczenie"): IEbiField => ({
  code: "rachunek",
  type: "text",
  label,
  hint: "Nieobowiązkowo.",
  maxLength: 40,
  placeholder: "PL00 0000 0000 0000 0000 0000 0000",
});
